import { ref, reactive, computed, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  ListShopForTransPickupRequestDocument,
  type ListShopForTransPickupRequestQuery,
  type ShopListForTransPickupRequestInput,
  GetChainPaymentMethodDocument,
  type GetChainPaymentMethodQuery,
  GetChainDocument,
  type GetChainQuery,
  type TransPickupRequestCreateInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import { getWordList } from "@/common/graphql/expression";
import { DELIVERY_MOVE_REGISTER_FILE_NAME } from "../constants";
import type { ShopOption } from "../types";

/** 画面初期ロードに必要な依存をまとめた型。 */
interface UseDeliveryMoveRegisterInitParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  transPickupRequestCreateInput: TransPickupRequestCreateInput;
  shopSelectValues_from: Ref<ShopOption>;
  shopSelectValues_to: Ref<ShopOption>;
  shopData_from_address: Ref<string>;
  shopData_from_address1: Ref<string>;
  shopData_to_address: Ref<string>;
  shopData_to_address1: Ref<string>;
  deliveryAreaName: Ref<string>;
  display_delivery_time: Ref<string>;
  dummy_payment_amount: Ref<string>;
  wordList: Ref<Record<string, string>>;
}

/** 画面初期ロードを扱う composable。 */
export const useDeliveryMoveRegisterInit = (params: UseDeliveryMoveRegisterInitParams) => {
  const {
    errdialog,
    errMessage,
    transPickupRequestCreateInput,
    shopSelectValues_from,
    shopSelectValues_to,
    shopData_from_address,
    shopData_from_address1,
    shopData_to_address,
    shopData_to_address1,
    deliveryAreaName,
    display_delivery_time,
    dummy_payment_amount,
    wordList,
  } = params;

  // Fieldのname属性で使用される必要なラベルキー（ShopRegister.vueと同様のパターン）
  const requiredFieldLabelKeys = [
    'lbl_delivery_source',
    'lbl_delivery_destination',
    'lbl_area',
    'lbl_delivery_time_select',
    'lbl_pay_method',
    'lbl_pay_money',
    'lbl_cash_register_no',
    'lbl_receipt_no',
    'lbl_memo',
  ] as const;

  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return requiredFieldLabelKeys.every((key) => key in wordList.value);
  });

  // 決済方法
  const paymentMethodItems = ref<(string | undefined)[]>([]);
  let _paymentList: GetChainPaymentMethodQuery['getChainPaymentMethod'] = [];
  const paymentRequiredFlg = ref(false);

  // 個口数リスト
  const unitItems = reactive<number[]>([]);

  // 店舗一覧 input
  const shopFromListInput: ShopListForTransPickupRequestInput = {
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    to_shop_code: '',
    from_shop_code: ''
  };

  // 店舗(配達元)
  const shopFromListItems = ref<ShopOption[]>([]);

  // 初期処理
  async function initialize(): Promise<void> {
    transPickupRequestCreateInput.chain_code = localStorageGetChainCode();
    transPickupRequestCreateInput.customer_code = localStorageGetCustomerCode();

    shopSelectValues_from.value = { shop_code: '', name: '' };

    shopData_from_address.value = '';
    shopData_from_address1.value = '';
    shopSelectValues_to.value = { shop_code: '', name: '' };

    shopData_to_address.value = '';
    shopData_to_address1.value = '';

    deliveryAreaName.value = '';
    display_delivery_time.value = '';

    dummy_payment_amount.value = '';
  }

  // 初期画面
  async function initOnMounted(): Promise<void> {
    try {
      await initialize();

      const apolloClient = await generateApolloClient();

      //文言データを取得する
      wordList.value = await getWordList(
        localStorageGetChainCode(),
        DELIVERY_MOVE_REGISTER_FILE_NAME,
        localStorageGetCustomerCode()
      );

      /**
       * 店舗データ取得処理
       */
      shopFromListInput.from_shop_code = localStorageGetShopCode();
      const listShop: ApolloQueryResult<ListShopForTransPickupRequestQuery> =
        await apolloClient.query<ListShopForTransPickupRequestQuery>({
          query: ListShopForTransPickupRequestDocument,
          variables: {
            input: shopFromListInput
          }
        });
      shopFromListItems.value = listShop.data.listShopForTransPickupRequest as ShopOption[] | null;

      /**
       * チェーンごと支払方法取得
       */
      const paymentList: ApolloQueryResult<GetChainPaymentMethodQuery> =
        await apolloClient.query<GetChainPaymentMethodQuery>({
          query: GetChainPaymentMethodDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode()
          }
        });
      _paymentList = paymentList.data.getChainPaymentMethod;
      paymentMethodItems.value = _paymentList?.map((payment) => payment?.payment_method) ?? [];

      const max_unit: ApolloQueryResult<GetChainQuery> = await apolloClient.query<GetChainQuery>({
        query: GetChainDocument,
        variables: {
          chain_code: transPickupRequestCreateInput.chain_code,
          customer_code: transPickupRequestCreateInput.customer_code
        }
      });
      for (let i = 0; i < Number(max_unit.data.getChain!.max_unit); i++) {
        unitItems.push(i + 1);
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : '予期せぬエラーが発生しました。';
      return;
    }
  }

  return {
    wordList,
    isWordListReady,
    paymentMethodItems,
    get _paymentList() { return _paymentList; },
    set _paymentList(val) { _paymentList = val; },
    paymentRequiredFlg,
    unitItems,
    shopFromListInput,
    shopFromListItems,
    initialize,
    initOnMounted,
  };
};
